import { Dialog, DialogActions, DialogContent } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  BugReportDialogHeader,
  BugReportMainFields,
  CurrentUrlBadge,
  HarFileSection,
  ScreenshotSection,
  VideoLinkSection,
} from './components';
import { useBugReport } from './utils';

type P = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onMinimize: () => void;
  readonly screenshot: File | undefined;
  readonly setScreenshot: (f: File | undefined) => void;
};

export function BugReportDialog({
  isOpen,
  onClose,
  onMinimize,
  screenshot,
  setScreenshot,
}: P) {
  const { t } = useTranslation('bugReport');

  const {
    currentUser,
    derivedName,
    form,
    handleAttachFile,
    handleCaptureScreenshot,
    handleMinimize,
    handleSilentClose,
    harFile,
    isBusy,
    isCapturing,
    isHarInstructionsOpen,
    isNameSystemAccount,
    isSubmitting,
    isVideoLinkVisible,
    setHarFile,
    setIsHarInstructionsOpen,
    setIsVideoLinkVisible,
    titleInputRef,
  } = useBugReport({ isOpen, onClose, onMinimize, screenshot, setScreenshot });

  return (
    <>
      <Dialog
        aria-labelledby="bug-report-dialog-title"
        fullWidth
        maxWidth="lg"
        open={isOpen}
        onClose={handleSilentClose}
      >
        <BugReportDialogHeader
          onMinimize={handleMinimize}
          onSilentClose={handleSilentClose}
        />

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <DialogContent className="overflow-hidden px-4 py-0">
            <div className="grid grid-cols-[2fr_1fr] gap-8">
              <BugReportMainFields
                currentUserEmail={currentUser?.email}
                form={form}
                isNameAutoDetected={Boolean(derivedName)}
                isNameSystemAccount={isNameSystemAccount}
                isSubmitting={isSubmitting}
                titleInputRef={titleInputRef}
              />

              <aside className="flex flex-col gap-8">
                <CurrentUrlBadge />

                <div className="flex flex-1 flex-col gap-8 border-l border-tiga-border-lighter pt-2 pl-8">
                  <ScreenshotSection
                    isBusy={isBusy}
                    isCapturing={isCapturing}
                    isSubmitting={isSubmitting}
                    screenshot={screenshot}
                    setScreenshot={setScreenshot}
                    onAttachFile={handleAttachFile}
                    onCapture={() => void handleCaptureScreenshot()}
                  />

                  <VideoLinkSection
                    form={form}
                    isSubmitting={isSubmitting}
                    isVisible={isVideoLinkVisible}
                    setIsVisible={setIsVideoLinkVisible}
                  />

                  <HarFileSection
                    harFile={harFile}
                    isBusy={isBusy}
                    isInstructionsOpen={isHarInstructionsOpen}
                    setHarFile={setHarFile}
                    toggleInstructions={() =>
                      setIsHarInstructionsOpen((open) => !open)
                    }
                    onAttachFile={handleAttachFile}
                  />
                </div>
              </aside>
            </div>
          </DialogContent>

          <DialogActions className="p-0">
            <form.AppForm>
              <form.ActionsRow
                primaryAction={{ text: t(($) => $.dialog.submitButton.text) }}
                onCancel={handleSilentClose}
              />
            </form.AppForm>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
